import BottomSheetContainer from "@app/sharedComponents/Bottomsheet/Bottomsheet";
import { useEffect, useMemo, useState } from "react"
import { View, Image, TouchableOpacity, ActivityIndicator, Platform } from "react-native"
import { Theme, ThemedText, ThemedView } from '@app/theme'
import RightArrow from "@app/assets/svg/RightArrow";
import Button from "@app/sharedComponents/Button/Button";
import Copy from "@app/assets/svg/Copy";
import Share from "@app/assets/svg/Share";
import DeviceMobile from "@app/assets/svg/DeviceMobile";
import Whatsapp from "@app/assets/svg/Whatsapp";
import { useTheme } from '@shopify/restyle';
import { generateShareLinkV2Utils } from "./utils";
import { useAppSelector } from "@app/store/hooks";
import Clipboard from '@react-native-clipboard/clipboard';
import { QUERY_PARAMS } from "@app/configs/constants";
import { shareLinkAllowedPlatform } from "@app/utils/utils";


interface MFDShareLinkProps {
 onClickCaptureOnDevice?: () => void
}

const MFDShareLink: React.FC<MFDShareLinkProps> = ({
 onClickCaptureOnDevice
}) => {
    const [showShareModal, setShowShareModal] = useState(false);
    const [copyLoading, setCopyLoading] = useState(false);
    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const {userDetails} = useAppSelector(state => state.auth);
    const [copied, setCopied] = useState(false);
    const [platformConfig, setPlatformConfig] = useState(undefined);
    const { colors } = useTheme<Theme>();

    const renderLogo = useMemo(() => (
        <>
            <Image
                source={{
                    uri: 'https://volt-images-alpha.s3.ap-south-1.amazonaws.com/kyc_camera.png'
                }}
                style={{
                    width: 64,
                    height: 64,
                    borderRadius: 5
                }}
            />
        </>
    ), [])

    useEffect(() => {
        if(copied){
            setTimeout(() => {
                setCopied(false)
            }, 3000)
        }
    }, [copied])

    const handleCopyLink = async () => {
        setCopied(true);
        setCopyLoading(true);
        const link = await generateShareLinkV2Utils({ userDetails: userDetails });
        Clipboard.setString(
            platformConfig !== undefined
                ? link + `${platformConfig !== undefined ? `&${platformConfig}&${QUERY_PARAMS.IS_CUSTOMER}=true` : ''}`
                : link
        );
        setCopyLoading(false);
    }

    const handleShareWhatsapp = async () => {
        setWhatsappLoading(true);
        const link = await generateShareLinkV2Utils({userDetails: userDetails});

        let borrowerName = userDetails?.user?.fullName;
        let referalLink = platformConfig !== undefined
            ? link + `${platformConfig !== undefined ? `&${platformConfig}&${QUERY_PARAMS.IS_CUSTOMER}=true` : ''}`
            : link;
        let partnerName = userDetails?.linkedPartnerAccounts[0]?.partnerName || 'testPartnerName';

        let message = `Dear ${borrowerName},\n\nAs part of the KYC process, please complete the selfie verification step at your earliest convenience. Click the link below to proceed:\n\n${referalLink}\n\nThis step is essential to finalize your application. If you have any questions, feel free to reach out. \n\nThank you,\n${partnerName}`;
        if(Platform.OS === 'web'){
            window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`, '_blank')
        }
        setWhatsappLoading(false);
    }

    const renderCard = ({
        title,
        subtitle,
        icon,
        clickAction
    }: {
        title: string,
        subtitle: string,
        icon: React.ReactNode,
        clickAction: () => void
    }) => {
        return (
            <>
                <TouchableOpacity style={{ flex: 1 }} onPress={() => { clickAction() }}>
                    <ThemedView
                        borderColor={'border-secondary'}
                        borderWidth={1}
                        borderRadius={'sm'}
                        padding={'md'}
                    >
                        <ThemedView style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%'
                        }}>
                            <ThemedView style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                flex: 1,
                                paddingRight: 30
                            }}>
                                <ThemedView style={{
                                    padding: 10,
                                    marginRight: 12
                                }}
                                    flexDirection={'row'}
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                    bg='bg-fill-emphasis-primary-1'
                                    borderColor={'border-secondary'}
                                    borderWidth={1}
                                    borderRadius={'sm'}
                                >
                                    {icon}
                                </ThemedView>
                                <ThemedView style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <ThemedView style={{ flex: 1 }}>
                                        <ThemedText
                                            variant={'b3s'}
                                            color={'text-primary'}
                                        >
                                            {title}
                                        </ThemedText>
                                    </ThemedView>
                                    <ThemedView style={{ marginTop: 4 }} />
                                    <ThemedView style={{ flex: 1 }}>
                                        <ThemedText
                                            variant={'b4r'}
                                            color={'text-secondary'}
                                        >
                                            {subtitle}
                                        </ThemedText>
                                    </ThemedView>
                                </ThemedView>
                            </ThemedView>
                            <ThemedView>
                                <RightArrow size={16} color={colors['bg-action-primary']} />
                            </ThemedView>
                        </ThemedView>
                    </ThemedView>
                </TouchableOpacity>
            </>
        )
    }



    useEffect(() => {
        if (Platform.OS === 'web' && window?.location?.search) {
            const searchParams = new URLSearchParams(window.location.search);
            const platform = searchParams.get(QUERY_PARAMS.PLATFORM);
            if (platform && shareLinkAllowedPlatform(platform)) {
                searchParams.delete(QUERY_PARAMS.PLJ);
                searchParams.delete(QUERY_PARAMS.PLJ_SHORT_TOKEN);
                searchParams.delete(QUERY_PARAMS.CUSTOMER_SSO_TOKEN);
                //BE already provide platfrom from link api
                searchParams.delete(QUERY_PARAMS.PLATFORM);
                searchParams.delete(QUERY_PARAMS.IS_CUSTOMER);
                searchParams.delete(QUERY_PARAMS.SSO_TOKEN);
                //@ts-expect-error
                setPlatformConfig(searchParams.toString());
            }
        }
    }, [])


    const shareModal = () => {
        return (
            <BottomSheetContainer
                isVisible={showShareModal}
                close={() => {
                    setShowShareModal(false)
                }}
                onClose={() => {
                    setShowShareModal(false)
                }}
                closeOnBackdropPress={false}
                borderTopRightRadius={10}
                borderTopLeftRadius={10}
                showCloseIcon={true}
                dragComponent={<View style={{ width: 40, height: 4, backgroundColor: 'gray', borderRadius: 10 }} />}
                containerStyle={{ backgroundColor: 'white' }}
            >
                <ThemedView style={{ display: 'flex', flexDirection: 'column' }} padding={'lg'}>
                    <ThemedText variant={'h5s'} color={'text-primary'}>
                        {'Share link with customers'}
                    </ThemedText>
                    <ThemedView style={{ marginTop: 24 }} />
                    <Button
                        label={copied ? 'Link copied' : 'Copy link'}
                        type="SOLID"
                        size='MD'
                        disabled={false}
                        handlePress={() => {
                            handleCopyLink()
                        }}
                        showLeftAccessory={true}
                        leftAccessory={()=>{
                            return <Copy size={16} color={'white'} />
                        }}
                        textColor={'white'}
                        buttonLoader={copyLoading}
                    />
                    <View style={{ marginTop: 16 }} />
                    <Button
                        label={'Share on WhatsApp'}
                        type="OUTLINED"
                        size='MD'
                        disabled={false}
                        handlePress={() => {
                            handleShareWhatsapp()
                        }}
                        showLeftAccessory={true}
                        leftAccessory={()=><Whatsapp />}
                        buttonLoader={whatsappLoading}
                    />
                </ThemedView>
            </BottomSheetContainer>
        )
    }


    return (
        <ThemedView>
            <ThemedView
                style={{
                    paddingHorizontal: 16,
                    marginTop: 24,
                    marginBottom: 24
                }}
            >
                {renderLogo}
                <ThemedView style={{ marginTop: 4 }}>
                    <ThemedView >
                        <ThemedText
                            variant={'h5s'} 
                            mt={'xs'}
                        >
                            {'Capture customer selfie'}
                        </ThemedText>
                        <ThemedView style={{ marginTop: 4 }} />
                        <ThemedText
                            variant={'b3r'}
                            color={'text-primary'}
                        >
                            {'Take customer selfie for KYC. Refresh page after completion.'}
                        </ThemedText>
                        <ThemedView style={{ marginTop: 32 }} />
                        {
                            renderCard({
                                title: 'Share link with customer',
                                subtitle: 'Customer will capture selfie on their device.',
                                icon: <Share />,
                                clickAction: async () => {
                                    await setShowShareModal(true)
                                }
                            })
                        }
                        <ThemedView style={{ height: 16 }} />
                        {
                            renderCard({
                                title: 'Capture on this device',
                                subtitle: 'Take a live photo of customer from this device.',
                                icon: <DeviceMobile />,
                                clickAction: () => {
                                    onClickCaptureOnDevice &&
                                    onClickCaptureOnDevice()
                                }
                            })
                        }
                    </ThemedView>
                </ThemedView>
            </ThemedView>
            {shareModal()}
        </ThemedView>
    )
}   

export default MFDShareLink;